/* ===========================================================================
   Les mailles d'Agat — COMPORTEMENT DE L'EN-TÊTE
   ---------------------------------------------------------------------------
   Chargé en `defer` par snippets/lmda-etiquette.liquid, notre point d'entrée
   dans sections/header.liquid (le fichier cœur de Dawn reste intact).

   Un seul rôle, repris du site vitrine (src/layouts/BaseLayout.astro, classe
   `is-scrolled` posée sur la barre dès que `hero.bottom <= 80`) : dire à la
   barre du haut si elle est posée sur le ciel du hero — elle se fait alors
   transparente — ou sur le corps de la page, où elle reprend son fond crème.

   L'habillage vit dans assets/lmda-header.css § 4. Deux classes, et le sens de
   chacune compte :

     html.lmda-header-live            posée ici, et seulement si un hero est
                                      présent ET que ce script tourne. C'est le
                                      garde-fou : sans elle le CSS ne rend
                                      JAMAIS la barre transparente. Un script
                                      absent, bloqué ou en erreur laisse donc
                                      une barre crème lisible, jamais une barre
                                      fantôme au-dessus du contenu.
     .section-header.lmda-header-solid l'état courant. Posée d'emblée (l'état
                                      sûr) puis RETIRÉE tant qu'un hero occupe
                                      le haut de l'écran — jamais l'inverse.

   `.section-header` est bien l'élément à marquer : c'est lui qui porte le
   `position: sticky` (bloc {% style %} de header.liquid) et lui que le
   `StickyHeader` de Dawn manipule (`shopify-section-header-sticky`). Le fond,
   en revanche, reste sur son enfant `.header-wrapper` — cf. le commentaire du
   CSS (teinte de la status bar iOS).
   =========================================================================== */

(() => {
  'use strict';

  /* Hauteur sous laquelle le bas du hero est considéré « passé sous la barre ».
     80 px : la valeur du site (barre mesurée ~86 px, et le fondu du bas du hero
     fait au moins 130 px de haut — l'écart ne se voit pas). */
  const GAP = 80;

  const HERO_SELECTOR = '.lmda-hero-section';
  const HEADER_SELECTOR = '.section-header';
  const LIVE_CLASS = 'lmda-header-live';
  const SOLID_CLASS = 'lmda-header-solid';

  class LmdaHeader {
    constructor() {
      /* les hero actuellement à cheval sur le haut de l'écran (l'éditeur de
         thème permet d'en poser plusieurs sur une même page) */
      this.visible = new Set();
      this.header = null;
      this.observer = null;
      this.handleIntersect = this.handleIntersect.bind(this);
      this.refresh = this.refresh.bind(this);

      this.refresh();

      /* Éditeur de thème : ajouter, retirer ou déplacer une section réécrit le
         DOM (le hero comme l'en-tête). On refait le tour à chaque fois. */
      document.addEventListener('shopify:section:load', this.refresh);
      document.addEventListener('shopify:section:unload', this.refresh);
      document.addEventListener('shopify:section:reorder', this.refresh);
    }

    refresh() {
      this.header = document.querySelector(HEADER_SELECTOR);

      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this.visible.clear();

      if (!this.header) return;

      /* L'état sûr d'abord : la barre est solide. On ne la rendra transparente
         qu'après avoir trouvé un hero à surveiller. */
      this.header.classList.add(SOLID_CLASS);

      const heroes = document.querySelectorAll(HERO_SELECTOR);

      if (!heroes.length || !('IntersectionObserver' in window)) {
        /* Pages sans hero (collection, fiche produit, panier, pages libres…),
           ou navigateur sans IntersectionObserver : rien à surveiller, la barre
           reste crème du premier au dernier pixel — aucun clignotement. */
        document.documentElement.classList.remove(LIVE_CLASS);
        return;
      }

      document.documentElement.classList.add(LIVE_CLASS);

      /* Premier état posé à la main : le premier rappel de l'observateur est
         asynchrone, et sur une page rouverte en cours de défilement on verrait
         la barre changer d'avis. Même test que le site. */
      heroes.forEach((hero) => {
        if (hero.getBoundingClientRect().bottom > GAP) this.visible.add(hero);
      });
      this.apply();

      this.observer = new IntersectionObserver(this.handleIntersect, {
        /* On rétrécit le haut de la fenêtre de GAP px : le hero cesse alors
           d'« intersecter » exactement quand son bas passe au-dessus de GAP,
           c'est-à-dire quand il disparaît sous la barre. */
        rootMargin: `-${GAP}px 0px 0px 0px`,
        threshold: 0,
      });
      heroes.forEach((hero) => this.observer.observe(hero));
    }

    handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.visible.add(entry.target);
        else this.visible.delete(entry.target);
      });
      this.apply();
    }

    /* transparente tant qu'un hero occupe le haut de l'écran, crème sinon */
    apply() {
      if (this.header) this.header.classList.toggle(SOLID_CLASS, this.visible.size === 0);
    }
  }

  /* L'éditeur de thème peut réinjecter le snippet — et donc rejouer ce script.
     Une seule instance : la seconde exécution se contente de la relancer. */
  if (window.lmdaHeader) window.lmdaHeader.refresh();
  else window.lmdaHeader = new LmdaHeader();
})();
